import moment from "moment-timezone";
import { eventoVacio, TIPO_MAPA_ZONAS } from "./schema";

const ZONA = "America/Buenos_Aires";
const FORMATO_API = "YYYY-MM-DDTHH:mm:ss-03:00";

/* Traducción entre el formulario y el API. Único lugar donde se normalizan
 * fechas y horas: antes esta lógica estaba copiada literal en CrearEvento.js y
 * en SetEvent.js, y cualquier arreglo había que hacerlo dos veces. */

/* Una fecha del formulario son campos sueltos ("2026-12-04", "22:00", "04:00").
 * Si la hora de fin es menor a la de inicio, el evento cruza la medianoche y
 * termina al día siguiente.
 *
 * Los campos se leen siempre en hora de Argentina, no en la del navegador:
 * alguien cargando el evento desde otro huso lo guardaba con el día corrido. */
const enArgentina = (valor) => moment.tz(valor, ZONA);

const fechaAPayload = (item) => {
  const fechaEvento = enArgentina(item.fecha);
  const cruzaMedianoche = item.hora_fin < item.hora_inicio;
  const fechaFin = cruzaMedianoche
    ? fechaEvento.clone().add(1, "day")
    : fechaEvento.clone();

  const inicio = enArgentina(`${item.fecha}T${item.hora_inicio}`);

  return {
    id: item.id || null,
    fecha: fechaEvento.format(FORMATO_API),
    hora_inicio: inicio.format(FORMATO_API),
    hora_fin: enArgentina(`${fechaFin.format("YYYY-MM-DD")}T${item.hora_fin}`)
      .format(FORMATO_API),
    // sin acceso anticipado, el acceso abre cuando arranca el evento
    acceso_inicio: item.acceso_inicio
      ? enArgentina(item.acceso_inicio).format(FORMATO_API)
      : inicio.format(FORMATO_API),
    oculta: item.oculta === true,
  };
};

export const toPayload = (form) => ({
  evento: {
    evento: form.evento,
    descripcion: form.descripcion,
    eventos_tipos_id: form.eventos_tipos_id,
    privado: Boolean(form.privado),
    direccion: form.direccion,
    ciudad: form.ciudad,
    tipo_mapa: form.tipoMapa || TIPO_MAPA_ZONAS,
  },
  /* El orden manda: el backend decide avatar y portada a partir de la posición.
   * No se envían esos flags desde acá para que la regla viva en un solo lado. */
  attachs: form.media.map((item) => ({
    url: item.url,
    type: item.type,
    format: item.format,
    public_id: item.public_id,
  })),
  fechas: (form.fechas || []).map(fechaAPayload),
  sponsors: (form.sponsors || [])
    .filter((sponsor) => sponsor.nombre && sponsor.url)
    .map((sponsor, index) => ({
      // los ids temporales del front no son ids de la base
      ...(typeof sponsor.id === "number" ? { id: sponsor.id } : {}),
      nombre: sponsor.nombre,
      url: sponsor.url,
      type: sponsor.type,
      format: sponsor.format,
      public_id: sponsor.public_id,
      orden: index + 1,
    })),
});

/* Camino inverso: la respuesta del API a los campos del formulario. */
export const fromEvento = (evento) => {
  if (!evento) return eventoVacio();

  return {
    ...eventoVacio(),
    evento: evento.evento ?? "",
    descripcion: evento.descripcion ?? "",
    eventos_tipos_id: evento.eventos_tipos_id ?? "",
    privado: evento.privado ?? null,
    direccion: evento.direccion ?? "",
    ciudad: evento.ciudad ?? "",
    tipoMapa: evento.tipo_mapa || TIPO_MAPA_ZONAS,

    fechas: evento.fechas?.length
      ? evento.fechas.map((fecha) => ({
          id: fecha.id,
          fecha: moment(fecha.fecha).tz(ZONA).format("YYYY-MM-DD"),
          hora_inicio: moment(fecha.hora_inicio).tz(ZONA).format("HH:mm"),
          hora_fin: moment(fecha.hora_fin).tz(ZONA).format("HH:mm"),
          acceso_inicio: fecha.acceso_inicio
            ? moment(fecha.acceso_inicio).tz(ZONA).format("YYYY-MM-DDTHH:mm")
            : "",
          oculta: fecha.oculta === true,
        }))
      : eventoVacio().fechas,

    /* El orden en el formulario ES el rol: primero el avatar, segundo la
     * portada. La base los devuelve por id, así que hay que reordenarlos por
     * sus flags o al guardar de nuevo se intercambiarían los roles. */
    media: [...(evento.attachs ?? [])]
      .map((attach, indice) => ({ attach, indice }))
      .sort((a, b) => {
        const rol = ({ attach }) => (attach.avatar ? 0 : attach.banner ? 1 : 2);
        return rol(a) - rol(b) || a.indice - b.indice;
      })
      .map(({ attach }) => ({
        url: attach.url,
        type: attach.type,
        format: attach.format,
        public_id: attach.public_id,
      })),

    sponsors: (evento.sponsors ?? []).map((sponsor, index) => ({
      ...sponsor,
      orden: sponsor.orden ?? index + 1,
      isNew: false,
    })),
  };
};
